import { NgModule, Optional, SkipSelf } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { RoutePath } from '@nano/common/routing/routing';
import { ErrorPageComponent } from '@nano/core/error';

import { FeatureEnabledGuard } from './guards/feature-enabled.guard';
import { LoggedInGuard } from './guards/logged-in.guard';
import { NotLoggedInGuard } from './guards/not-logged-in.guard';
import { UserVerified } from './guards/user-verified.guard';
import { HasAccount } from './guards/has-account.guard';

export const routes: Routes = [
  {
    path: RoutePath.signup,
    loadChildren: () => import('@nano/registration').then((m) => m.RegistrationModule),
    canMatch: [FeatureEnabledGuard],
    canLoad: [FeatureEnabledGuard],
    canActivate: [FeatureEnabledGuard]
  },
  {
    path: RoutePath.login,
    loadChildren: () => import('@nano/login').then((m) => m.LoginModule)
  },
  {
    path: RoutePath.passwordRecovery,
    loadChildren: () => import('@nano/forgot-password').then((m) => m.ForgotPasswordModule),
    canMatch: [FeatureEnabledGuard],
    canLoad: [NotLoggedInGuard, FeatureEnabledGuard],
    canActivate: [FeatureEnabledGuard]
  },
  {
    path: RoutePath.accounts,
    loadChildren: () => import('@nano/account').then((m) => m.AccountModule),
    canLoad: [LoggedInGuard],
    canActivate: [LoggedInGuard]
  },
  {
    path: RoutePath.verification,
    loadChildren: () => import('@nano/verification').then((m) => m.VerificationModule),
    canLoad: [LoggedInGuard],
    canActivate: [LoggedInGuard]
  },
  {
    path: RoutePath.cards,
    loadChildren: () => import('@nano/cards').then((m) => m.CardsModule),
    canMatch: [FeatureEnabledGuard],
    canLoad: [LoggedInGuard, FeatureEnabledGuard, UserVerified],
    canActivate: [LoggedInGuard, FeatureEnabledGuard, UserVerified]
  },
  {
    path: RoutePath.paymentImport,
    loadChildren: () => import('@nano/payment-import').then((m) => m.PaymentImportModule),
    canMatch: [FeatureEnabledGuard],
    canLoad: [LoggedInGuard, FeatureEnabledGuard, UserVerified],
    canActivate: [LoggedInGuard, FeatureEnabledGuard, UserVerified]
  },
  {
    path: RoutePath.scheduledPayments,
    loadChildren: () => import('@nano/scheduled-payments').then((m) => m.ScheduledPaymentsModule),
    canMatch: [FeatureEnabledGuard],
    canLoad: [LoggedInGuard, FeatureEnabledGuard, UserVerified],
    canActivate: [LoggedInGuard, FeatureEnabledGuard, UserVerified]
  },
  {
    path: RoutePath.pendingPayments,
    loadChildren: () => import('@nano/pending-payments').then((m) => m.PendingPaymentsModule),
    canMatch: [FeatureEnabledGuard],
    canLoad: [LoggedInGuard, FeatureEnabledGuard, HasAccount],
    canActivate: [LoggedInGuard, FeatureEnabledGuard, HasAccount]
  },
  {
    path: RoutePath.loans,
    loadChildren: () => import('@nano/loans').then((m) => m.LoansModule),
    canMatch: [FeatureEnabledGuard],
    canLoad: [LoggedInGuard, FeatureEnabledGuard],
    canActivate: [LoggedInGuard, FeatureEnabledGuard]
  },
  {
    path: RoutePath.recipients,
    loadChildren: () => import('@nano/payment-beneficiaries').then((m) => m.PaymentBeneficiariesModule),
    canMatch: [FeatureEnabledGuard],
    canLoad: [LoggedInGuard, FeatureEnabledGuard],
    canActivate: [LoggedInGuard, FeatureEnabledGuard]
  },
  {
    path: RoutePath.settings,
    loadChildren: () => import('@nano/settings').then((m) => m.SettingsModule),
    canLoad: [LoggedInGuard],
    canActivate: [LoggedInGuard]
  },
  {
    path: RoutePath.messages,
    loadChildren: () => import('@nano/messages').then((m) => m.MessagesModule),
    canMatch: [FeatureEnabledGuard],
    canLoad: [LoggedInGuard, FeatureEnabledGuard],
    canActivate: [LoggedInGuard, FeatureEnabledGuard]
  },
  { path: RoutePath.root, pathMatch: 'full', redirectTo: RoutePath.login },
  { path: RoutePath.wildcard, component: ErrorPageComponent }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class RoutingModule {
  constructor(@Optional() @SkipSelf() parentModule: RoutingModule) {
    if (parentModule) {
      throw new Error('RoutingModule is already loaded, import it only once.');
    }
  }
}
